<template>
  <div class="stl-canvas-wrapper">
    <div v-if="isLoading" class="loading-overlay">
      <div class="loading-box">
        <div class="spinner"></div>
        <div class="loading-text">{{ loadingText }}</div>
      </div>
    </div>
    <div ref="container" class="stl-canvas-container"></div>
  </div>
</template>

<script>
import * as THREE from 'three'
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js'
import { STLLoader } from 'three/examples/jsm/loaders/STLLoader.js'

export default {
  name: 'CustomFitStlCanvas',
  props: {
    stlUrl: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      isLoading: true,
      loadingText: 'Loading STL...',
      scene: null,
      camera: null,
      renderer: null,
      controls: null,
      frameId: null,
      resizeObserver: null,
    }
  },
  mounted() {
    this.initScene()
    console.log("STL URL", this.stlUrl)
    this.loadStl(this.stlUrl)
  },
  beforeUnmount() {
    this.cleanup()
  },
  methods: {
    initScene() {
      const container = this.$refs.container
      this.scene = new THREE.Scene()
      this.scene.background = new THREE.Color(0xf1f1f1)

      this.camera = new THREE.PerspectiveCamera(
        35,
        container.clientWidth / container.clientHeight,
        0.1,
        5000,
      )
      this.camera.position.set(0, 0, 200)

      this.renderer = new THREE.WebGLRenderer({ antialias: true })
      this.renderer.setPixelRatio(window.devicePixelRatio)
      this.renderer.setSize(container.clientWidth, container.clientHeight)
      container.appendChild(this.renderer.domElement)

      this.controls = new OrbitControls(this.camera, this.renderer.domElement)
      this.controls.enableDamping = true

      const ambientLight = new THREE.AmbientLight(0xffffff, 0.5)
      const directionalLight = new THREE.DirectionalLight(0xffffff, 0.8)
      directionalLight.position.set(1, 1, 2)
      this.scene.add(ambientLight)
      this.scene.add(directionalLight)

      window.addEventListener('resize', this.onResize)
      this.observeContainerResize()
      this.animate()
    },
    observeContainerResize() {
      if (!window.ResizeObserver || !this.$refs.container) return
      this.resizeObserver = new ResizeObserver(() => {
        this.onResize()
      })
      this.resizeObserver.observe(this.$refs.container)
    },
    loadStl(url) {
      const loader = new STLLoader()
      if (loader.setCrossOrigin) loader.setCrossOrigin('anonymous')
      loader.load(
        url,
        geometry => {
          geometry.computeVertexNormals()

          const material = new THREE.MeshStandardMaterial({
            color: 0xe5e5e5,
            metalness: 0.1,
            roughness: 0.7,
          })

          const mesh = new THREE.Mesh(geometry, material)
          mesh.rotation.x = -Math.PI / 2
          this.scene.add(mesh)

          const box = new THREE.Box3().setFromObject(mesh)
          const center = box.getCenter(new THREE.Vector3())
          const size = box.getSize(new THREE.Vector3())
          const maxDim = Math.max(size.x, size.y, size.z) || 1
          const fov = this.camera.fov * (Math.PI / 180)
          const cameraZ = (maxDim / (2 * Math.tan(fov / 2))) * 1.6

          this.camera.position.set(center.x, center.y, center.z + cameraZ)
          this.camera.near = Math.max(0.1, cameraZ / 100)
          this.camera.far = cameraZ * 100
          this.camera.updateProjectionMatrix()
          this.controls.target.copy(center)
          this.controls.update()

          this.loadingText = 'STL loaded'
          this.isLoading = false
        },
        undefined,
        error => {
          console.error('Error loading STL', error)
          this.loadingText = 'Failed to load STL'
          this.isLoading = false
        },
      )
    },
    onResize() {
      if (!this.renderer || !this.camera || !this.$refs.container) return
      const container = this.$refs.container
      this.camera.aspect = container.clientWidth / container.clientHeight
      this.camera.updateProjectionMatrix()
      this.renderer.setSize(container.clientWidth, container.clientHeight)
    },
    animate() {
      this.frameId = requestAnimationFrame(this.animate)
      if (this.controls) this.controls.update()
      if (this.renderer && this.scene && this.camera) {
        this.renderer.render(this.scene, this.camera)
      }
    },
    cleanup() {
      window.removeEventListener('resize', this.onResize)
      if (this.resizeObserver) {
        this.resizeObserver.disconnect()
        this.resizeObserver = null
      }
      if (this.frameId) cancelAnimationFrame(this.frameId)
      if (this.renderer) {
        this.renderer.dispose()
        if (this.renderer.domElement && this.renderer.domElement.parentNode) {
          this.renderer.domElement.parentNode.removeChild(this.renderer.domElement)
        }
      }
    },
  },
}
</script>

<style scoped>
.stl-canvas-wrapper {
  position: relative;
  width: 100%;
  height: 100%;
}

.stl-canvas-container {
  width: 100%;
  height: 100%;
  background: #f1f1f1;
  border-radius: 5px;
}

.loading-overlay {
  position: absolute;
  inset: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  background: rgba(241, 241, 241, 0);
  z-index: 2;
}

.loading-box {
  text-align: center;
}

.spinner {
  width: 36px;
  height: 36px;
  border: 4px solid #ddd;
  border-top: 4px solid #666;
  border-radius: 50%;
  margin: 0 auto 12px;
  animation: spin 1s linear infinite;
}

.loading-text {
  color: #666;
  font-size: 12px;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}
</style>
